import { tap,  takeUntil, debounceTime, flatMap, filter } from 'rxjs/operators';
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { StreamService } from "../services";
import { Subject, BehaviorSubject, forkJoin } from 'rxjs';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';
import { SelectMultipleUsersDialogComponent } from '../groups/selectMultipleUsersDialog.component';

@Component({
    styles: [`
        .mat-column-controls {
            max-width: 45px;
        }
    `],
    template: `
        <mat-card *ngIf="users" [formGroup]="form">
            <mat-card-header>
                <div fxLayout="column" fxFlexOffset.gt-md="10" fxFlex.gt-md="90">
                    <div>
                        <mat-form-field style="width: 350px;">
                            <input matInput formControlName="searchTerm" placeholder="Search by name">
                        </mat-form-field>
                    </div>
                    <div>
                        <label>Following this topic ({{totalUsers}})</label>
                        <div fxFlex fxLayoutAlign="end center">
                            <button mat-button matTooltip="Add to {{topic?.title}}" (click)="addMembers()">
                                <mat-icon>person_add</mat-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </mat-card-header>
            <mat-card-content>
                <div fxLayout="column" *ngIf="users">
                <mat-table #table 
                           [dataSource]="users" 
                           matSort
                           fxFlex.gt-md="80"
                           (matSortChange)="sortUsers($event)"
                           infiniteScroll 
                           [infiniteScrollThrottle]="300"
                           [infiniteScrollDistance]="1"
                           [infiniteScrollDisabled]="lastPage"
                           (scrolled)="nextPage()">
                    <ng-container matColumnDef="firstName">
                        <mat-header-cell *matHeaderCellDef mat-sort-header="firstNameLower">First Name</mat-header-cell>
                        <mat-cell *matCellDef="let row" style="overflow: hidden; white-space: nowrap;">
                            <div style="text-overflow: ellipsis; overflow: hidden;">{{row.firstName}}</div>
                        </mat-cell>
                    </ng-container>
                    <ng-container matColumnDef="lastName">
                        <mat-header-cell *matHeaderCellDef mat-sort-header="lastNameLower">Last Name</mat-header-cell>
                        <mat-cell *matCellDef="let row" style="overflow: hidden; white-space: nowrap;">
                            <div style="text-overflow: ellipsis; overflow: hidden;">{{row.lastName}}</div>
                        </mat-cell>
                    </ng-container>
                    <ng-container matColumnDef="emailAddress">
                        <mat-header-cell *matHeaderCellDef mat-sort-header="emailAddress">Email Address</mat-header-cell>
                        <mat-cell *matCellDef="let row" style="overflow: hidden; white-space: nowrap;">
                            <div style="text-overflow: ellipsis; overflow: hidden;">{{row.emailAddress}}</div>
                        </mat-cell>
                    </ng-container>
                    <ng-container matColumnDef="controls">
                        <mat-header-cell *matHeaderCellDef></mat-header-cell>
                        <mat-cell *matCellDef="let row">
                            <button mat-icon-button (click)="unfollow(row)" [matTooltip]="'Remove ' + row.firstName + ' '  + row.lastName + ' from ' + topic?.title">
                                <mat-icon>clear</mat-icon>
                            </button>
                        </mat-cell>
                    </ng-container>
                    <mat-header-row *matHeaderRowDef="['firstName', 'lastName', 'emailAddress', 'controls']"></mat-header-row>
                    <mat-row *matRowDef="let row; columns: ['firstName', 'lastName', 'emailAddress', 'controls'];"></mat-row>
                </mat-table>
                <div *ngIf="noTouching" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
                    <spinner syle="padding-top: 100px;"></spinner>
                </div>
                </div>
            </mat-card-content>
        </mat-card>
    `
})
export class EditTopicPeopleComponent implements OnInit, OnDestroy {
    private cleanupTime = new Subject<void>();
    params = new BehaviorSubject<any>({ page: 0, rows: 100 });
    users = new BehaviorSubject<any[]>([]);
    noTouching: boolean;
    form: FormGroup;
    topic: any;
    totalUsers: any;
    lastPage: boolean;

    constructor(private route: ActivatedRoute, 
                private fb: FormBuilder,
                private dialog: MatDialog, 
                private snackBar: MatSnackBar, 
                private streamService: StreamService) {}

    ngOnInit(): void {
        this.form = this.fb.group({ searchTerm: [''] });

        this.form.get('searchTerm').valueChanges.pipe(takeUntil(this.cleanupTime), debounceTime(1500))
        .subscribe(searchTerm => this.params.next(Object.assign({}, this.params.value, { searchTerm })));

        this.route.parent.params
        .pipe(
              flatMap(params => this.streamService.getStream(params.topicId)),
              tap(stream => this.topic = stream),
              takeUntil(this.cleanupTime)
        ).subscribe(() => this.params.next(this.params.value));

        this.params.pipe(
            filter(() => !!this.topic),
            flatMap(params => this.streamService.getStreamFollowers(this.topic.id, params)),
            tap(results => this.lastPage = results.last),
            tap(results => this.totalUsers = results.totalElements),
            tap(() => this.noTouching = false),
            takeUntil(this.cleanupTime)
        ).subscribe(results => this.users.next(this.users.value.concat(results.content)));
    }

    sortUsers(sort: Sort) {
        this.users.next([]);
        this.params.next(Object.assign({}, this.params.value, {
            page: 0,
            sort: sort.active,
            sortDirection: sort.direction.toUpperCase()
        }));
    }

    unfollow(user) {
        this.dialog.open(ConfirmationDialogComponent, { 
            data: {
                title: `Delete ${user.firstName} ${user.lastName} from ${this.topic.title}?`, 
                actionLabel: 'Delete'}
        }).afterClosed()
        .pipe(
            filter(doIt => doIt),
            flatMap(() => this.streamService.setSubscription(this.topic.id, user.id, false))
        )
        .subscribe(() => {
            const users = this.users.value;
            users.splice(this.users.value.indexOf(user), 1);
            this.users.next(users);
            this.totalUsers--;
            this.snackBar.open(`${user.firstName} ${user.lastName} has been removed from ${this.topic.title}`, 'OK', { duration: 3000});    
        });
    }

    nextPage() {
        this.params.next(Object.assign({}, this.params.value, { page: this.params.value.page++ }));
    }
  
    refetch() {
        this.users.next([]);
        this.params.next(Object.assign({}, this.params.value, { page: 0 }));
    }

    addMembers() {
        this.streamService.getStreamFollowers(this.topic.id, { page:0, rows: 1000 })
        .pipe(
            flatMap(results => this.dialog.open(SelectMultipleUsersDialogComponent, { data: { existingUserIds: results.content.map(user => user.id) } }).afterClosed()),
            filter(users => !!users),
            flatMap(users => forkJoin(users.map(user => this.streamService.setSubscription(this.topic.id, user.id, true)))),
            tap(() => this.refetch()),
        ).subscribe(results => this.snackBar.open(`${results.length} user${results.length > 1 ? 's' : ''} added to ${this.topic.title}.`, 'OK', { duration: 3000}));
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
} 